// frontend/src/components/common/Modal.jsx
// COMPONENTE MODAL REUTILIZABLE
import React, { useEffect } from 'react';
import styled from 'styled-components';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

// Pila de modales abiertos. Cada Modal registra su propio listener de Escape en
// `document`, así que con dos modales apilados —por ejemplo, dar de alta una obra
// desde el formulario de un parte— la MISMA tecla llegaba a los dos y cerraba
// ambos: se perdía lo cargado en el de abajo. Con la pila, Escape lo atiende
// sólo el de arriba.
//
// Es un array de identificadores y no un contador porque los modales no se
// desmontan necesariamente en orden inverso al que se montaron.
const pilaModales = [];
let proximoIdModal = 0;

// STYLED COMPONENTS
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ $backdropBg, theme }) => $backdropBg || theme.colors.overlayBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
  /* Allow override via prop ($zIndex) for nested modals; otherwise use theme zIndex.modal */
  z-index: ${(p) => p.$zIndex || ((p.theme && p.theme.zIndex && p.theme.zIndex.modal) ? p.theme.zIndex.modal : 1050)};
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  max-height: ${({ $maxHeight }) => $maxHeight || '85vh'};
  min-height: ${({ $minHeight }) => $minHeight || 'auto'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  width: ${({ size }) => {
    switch (size) {
      case 'sm': return '400px';
      case 'md': return '600px';
      case 'lg': return '800px';
      case 'xl': return '1000px';
      case 'xxl': return '1200px';
      default: return '600px';
    }
  }};
  max-width: 95vw;
`;

const ModalHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 600;
  flex-grow: 1;
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: none;
  font-size: 1.35rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0;
  margin-left: 0.5rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.txtPrincipal};
  }
`;

const CloseButtonAbsolute = styled(CloseButton)`
  position: absolute;
  right: 12px;
  top: 12px;
  margin-left: 0;
`;

const ModalBody = styled.div`
  padding: 1rem;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: visible;
`;

const Modal = ({
  isOpen = true,
  onClose,
  title,
  titulo, // alias in Spanish
  children,
  size = 'lg',
  maxHeight,
  minHeight,
  closeOnBackdropClick = true,
  zIndex, // optional override for backdrop z-index
  backdropBg, // optional override for backdrop background color
  onCerrar, // alias in Spanish
}) => {
  // Support both English and Spanish prop names for gradual migration
  const finalTitle = title || titulo;
  const finalOnClose = onClose || onCerrar;
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();
  const handleBackdropClick = buildOnBackdropClick(finalOnClose, () => closeOnBackdropClick && Boolean(finalOnClose));

  // Identidad estable de esta instancia dentro de la pila de modales.
  const idModalRef = React.useRef(null);
  if (idModalRef.current === null) {
    idModalRef.current = ++proximoIdModal;
  }

  // Alta y baja en la pila de modales abiertos.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = idModalRef.current;
    pilaModales.push(id);
    return () => {
      const indice = pilaModales.indexOf(id);
      if (indice !== -1) pilaModales.splice(indice, 1);
    };
  }, [isOpen]);

  // Manejar tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape' || !finalOnClose) return;
      // Sólo el modal de más arriba se cierra: si no, un Escape cerraría toda
      // la pila de un saque y el usuario perdería lo que venía cargando abajo.
      if (pilaModales[pilaModales.length - 1] !== idModalRef.current) return;
      finalOnClose();
    };

    const handleCloseModalEvent = (ev) => {
      try {
        // If the modal is open and a close request arrives, forward to onClose
        if (isOpen && finalOnClose) {
          console.debug('Modal: received gestion-taller:close-modal event, invoking onClose');
          finalOnClose();
        }
      } catch (err) {
        console.warn('Modal: error handling close-modal event', err);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('gestion-taller:close-modal', handleCloseModalEvent);
      // Prevenir scroll del body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('gestion-taller:close-modal', handleCloseModalEvent);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, finalOnClose]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop $zIndex={zIndex} $backdropBg={backdropBg} onClick={handleBackdropClick} onMouseDown={onBackdropMouseDown}>
      <ModalContainer size={size} $maxHeight={maxHeight} $minHeight={minHeight}>
        {finalTitle ? (
          <ModalHeader>
            <ModalTitle>{finalTitle}</ModalTitle>
            {finalOnClose && (
              <CloseButton onClick={finalOnClose} type="button">
                ×
              </CloseButton>
            )}
          </ModalHeader>
        ) : (
          // When no title is provided, render an absolute close button so embedded content
          // can render its own header inside the modal body without duplicating a top header.
          finalOnClose && <CloseButtonAbsolute onClick={finalOnClose} type="button">×</CloseButtonAbsolute>
        )}
        <ModalBody>
          {children}
        </ModalBody>
      </ModalContainer>
    </ModalBackdrop>
  );
};

export default Modal;
